import {
  SegmentDataStructureField,
  segmentDataStructureTypeToDataStructure,
} from "@redotech/redo-model/marketing/segments/segment-data-structures";
import {
  CustomerActivityType,
  CustomerDataSource,
} from "@redotech/redo-model/marketing/segments/segment-types";
import { memo, useMemo, useState } from "react";
import { RedoSingleSelectDropdownInputButton } from "../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "../../../flex";
import { schemaToCategories } from "../characteristics/customer-characteristics";
import { SegmentFieldSelectModal } from "../segment-field-select-modal";

export const SegmentActivityWhereConditionSelector = memo(
  function SegmentActivityWhereConditionSelector({
    selectedField,
    setSelectedField,
    activityType,
    availableDataSourcesForOrders,
    availableDataSourcesForMarketing,
  }: {
    selectedField: SegmentDataStructureField<string> | undefined;
    setSelectedField: (field: SegmentDataStructureField<string>) => void;
    activityType: CustomerActivityType;
    availableDataSourcesForOrders?: CustomerDataSource[];
    availableDataSourcesForMarketing?: CustomerDataSource[];
  }) {
    const [whereConditionSelectModalOpen, setWhereConditionSelectModalOpen] =
      useState(false);

    return (
      <>
        <SegmentActivityWhereConditionSelectModal
          activityType={activityType}
          availableDataSourcesForMarketing={availableDataSourcesForMarketing}
          availableDataSourcesForOrders={availableDataSourcesForOrders}
          fieldSelected={(field) => {
            setSelectedField(field);
            setWhereConditionSelectModalOpen(false);
          }}
          initialSelectedField={selectedField}
          isOpen={whereConditionSelectModalOpen}
          onModalCloseRequested={() => setWhereConditionSelectModalOpen(false)}
        />
        <Flex>
          <RedoSingleSelectDropdownInputButton<
            SegmentDataStructureField<string>
          >
            isDropdownOpen={false}
            onClick={() => setWhereConditionSelectModalOpen(true)}
            selectedItem={
              selectedField
                ? {
                    id: selectedField.name,
                    type: "text",
                    text: selectedField.name,
                    value: selectedField,
                  }
                : undefined
            }
            size="xs"
          />
        </Flex>
      </>
    );
  },
);

const SegmentActivityWhereConditionSelectModal = memo(
  function SegmentActivityWhereConditionSelectModal({
    isOpen,
    onModalCloseRequested,
    fieldSelected,
    initialSelectedField,
    activityType,
    availableDataSourcesForOrders,
    availableDataSourcesForMarketing,
  }: {
    isOpen: boolean;
    onModalCloseRequested: () => void;
    fieldSelected: (field: SegmentDataStructureField<string>) => void;
    initialSelectedField?: SegmentDataStructureField<string>;
    activityType: CustomerActivityType;
    availableDataSourcesForOrders?: CustomerDataSource[];
    availableDataSourcesForMarketing?: CustomerDataSource[];
  }) {
    // For where conditions, combine both arrays since we want all available fields
    const availableDataSources = useMemo(() => {
      return Array.from(
        new Set([
          ...(availableDataSourcesForOrders ?? []),
          ...(availableDataSourcesForMarketing ?? []),
        ]),
      );
    }, [availableDataSourcesForOrders, availableDataSourcesForMarketing]);

    const categories = useMemo(
      () =>
        schemaToCategories(
          segmentDataStructureTypeToDataStructure[activityType],
          availableDataSources,
        ),
      [activityType, availableDataSources],
    );

    return (
      <SegmentFieldSelectModal<SegmentDataStructureField<string>>
        categories={categories}
        conditionSelected={fieldSelected}
        initialSelectedCondition={initialSelectedField}
        isOpen={isOpen}
        onModalCloseRequested={onModalCloseRequested}
        submitText="Use field"
        title="Select field"
      />
    );
  },
);
